import { roleProviderId } from "@smart/bridge-smokeball-basic";
import { unique } from "@smart/itops-utils-basic";

import { GqlFieldValues, GqlGroupValues, GqlMatterLayout } from "../types";

export type ContactType = "person" | "company";

export const contactTypes: ContactType[] = ["person", "company"];

export const isContactDetailsGroup = (
  group: { type?: string | null } | undefined | null,
): boolean => group?.type === "contactDetails";

export const getContactDetailsType = (
  groupField?: { name?: string | null } | null,
): ContactType | undefined => {
  if (!groupField?.name) return undefined;
  if (groupField.name === "company" || groupField.name.startsWith("company/")) {
    return "company";
  }
  if (groupField.name === "person" || groupField.name.startsWith("person/")) {
    return "person";
  }
  return undefined;
};

// Use "contactDetails" group's field to denote the contact type (person or company)
export const buildContactDetailsGroupField = (contactType: ContactType) => ({
  name: contactType,
  type: "Role" as const,
  possibleValues: [] as string[],
});

export const getDefaultContactDetailsLabel = (roleName: string) =>
  `${roleName} details`;

export const contactDetailsStandardFields = [
  "person/title",
  "person/firstName",
  "person/middleName",
  "person/lastName",
  "person/email",
  "person/phone",
  "person/cell",
  "person/residentialAddress",
  "person/birthDate",
  "company/name",
  "company/type",
  "company/phone",
  "company/fax",
  "company/email",
  "company/businessAddress",
  "company/mailingAddress",
  "company/abn",
  "company/companyNumber",
];

export const getContactDetailsStandardFields = (contactType: ContactType) =>
  contactDetailsStandardFields.filter((name) =>
    name.startsWith(`${contactType}/`),
  );

export const getContactRoleLayouts = (
  matterLayouts: GqlMatterLayout[],
): GqlMatterLayout[] => {
  const roleLayouts = matterLayouts.filter(
    (layout) => layout.providerId === roleProviderId && !layout.parentId,
  );
  const names = unique(roleLayouts.map((layout) => layout.name));
  return names.map(
    (name) => roleLayouts.find((layout) => layout.name === name)!,
  );
};

export const buildContactDetailsRoleChange = ({
  group,
  fields,
  roleLayout,
}: {
  group: GqlGroupValues;
  fields: GqlFieldValues[];
  roleLayout: GqlMatterLayout;
}) => {
  const oldRoleName = group.layout?.displayName || group.layout?.name;
  const newRoleLabel = getDefaultContactDetailsLabel(
    roleLayout.displayName || roleLayout.name,
  );
  const shouldUseDefaultLabel =
    !!oldRoleName &&
    (group.label === oldRoleName ||
      group.label === getDefaultContactDetailsLabel(oldRoleName));

  const layout = {
    id: roleLayout.id,
    name: roleLayout.name,
    providerId: roleLayout.providerId,
    parentId: roleLayout.parentId || undefined,
    parentName: roleLayout.parentName || undefined,
    parentProviderId: roleLayout.parentProviderId || undefined,
  };

  return {
    label: shouldUseDefaultLabel ? newRoleLabel : group.label,
    layout,
    fieldsToRemap: fields.filter((field) => field.layout && field.field),
  };
};
